/**
 * Ubytovací a provozní řád — hash-route #info-rad.
 *
 * Obsah vychází z potvrzovacího e-mailu rezervace (Bookolo, 12. 10. 2026),
 * který hostům chodí dnes — tedy ze skutečných pravidel areálu, ne z odhadu.
 * Při změně pravidel upravit i tuto stránku a text potvrzení.
 */

type Block = { title: string; body: React.ReactNode }

const BLOCKS: Block[] = [
  {
    title: '1. Příjezd a odjezd',
    body: (
      <>
        <ul className="space-y-1.5 list-disc list-inside">
          <li><b>Rybářská chatka</b> — příjezd od 16:00, klíče odevzdáváte do 14:00 v den odjezdu. Rybařit můžete až do 16:00.</li>
          <li><b>Tiny house</b> — příjezd od 15:00, odjezd do 12:00.</li>
          <li><b>Karavan</b> — příjezd i odjezd ve 12:00.</li>
          <li><b>Bivak</b> — denní lov 7:00–19:00, noční lov 19:00–7:00.</li>
        </ul>
        <p className="mt-3">
          Potřebujete-li přijet dříve, zavolejte předem na{' '}
          <a href="tel:+420775433401" className="font-semibold text-ink-900 hover:text-brand-600">+420 775 433 401</a>{' '}
          — vyjdeme vstříc, pokud to obsazenost dovolí.
        </p>
        <p className="mt-2 text-brand-600 font-semibold">
          Pozdní vrácení klíčů od chatky je zpoplatněno částkou 200 Kč.
        </p>
      </>
    ),
  },
  {
    title: '2. Ohlášení při příjezdu',
    body: (
      <p>
        Po příjezdu se prosím ohlaste porybnému na{' '}
        <a href="tel:+420775433401" className="font-semibold text-ink-900 hover:text-brand-600">+420 775 433 401</a>.
        Předá vám klíče, ukáže vaše lovné místo a domluvíte odečet elektroměru.
      </p>
    ),
  },
  {
    title: '3. Elektřina u chatek — platí se zvlášť',
    body: (
      <>
        <p className="font-semibold text-ink-900">
          Cena elektřiny není zahrnuta v ceně ubytování. Hradí se při ukončení pobytu
          podle skutečné spotřeby.
        </p>
        <p className="mt-2">
          V každé chatce je elektroměr. <b>Vyfotografujte si jeho stav na začátku
          i na konci pobytu</b> — slouží jako podklad pro vyúčtování.
        </p>
        <p className="mt-2">
          Zásuvky v chatce jsou dimenzované <b>do 500 W</b>. Na vaření doporučujeme
          plynový vařič.
        </p>
      </>
    ),
  },
  {
    title: '4. Rybolov',
    body: (
      <>
        <ul className="space-y-1.5 list-disc list-inside">
          <li>Povolenka se kupuje při příjezdu, není součástí ceny ubytování.</li>
          <li><b>Chatky</b> — lov 24 hodin nonstop, povolenky se vydávají po 24 hodinách.</li>
          <li><b>Bivaky</b> — denní lov 7:00–19:00 nebo noční 19:00–7:00.</li>
          <li>Lovit lze <b>pouze na vyznačeném lovném místě</b> — mimo označená místa je lov zakázán.</li>
          <li>Místo je na břehu označeno z obou stran kolíky s číslem; zelený koridor na mapě vymezuje váš úsek břehu i vody.</li>
        </ul>
        <p className="mt-3 text-brand-600 font-semibold">
          Ke každé povolence se skládá vratná záloha 500–1 500 Kč v hotovosti.
        </p>
        <p className="mt-2">
          Podrobná pravidla lovu, manipulace s rybou a vybavení najdete v{' '}
          <a href="#pravidla-rybolov" className="font-semibold text-ink-900 hover:text-brand-600">
            Pravidlech rybolovu
          </a>.
        </p>
      </>
    ),
  },
  {
    title: '5. Vybavení chatky',
    body: (
      <p>
        Dvě patrové postele s matracemi, stůl, lednice, elektrické vytápění, vnitřní
        i venkovní osvětlení, krytá veranda a okno s okenicí. Povlečení a spacáky si
        vozí host sám. <b>Přistýlky a dětské postýlky nejsou k dispozici.</b>
      </p>
    ),
  },
  {
    title: '6. Zázemí areálu',
    body: (
      <ul className="space-y-1.5 list-disc list-inside">
        <li>WC, sprchy a sociální zázemí v areálu</li>
        <li>Wi-Fi a parkování v areálu zdarma</li>
        <li>Dvoukolák na převoz vybavení — zapůjčíme zdarma</li>
        <li>Obchod se základním rybářským vybavením přímo v areálu</li>
        <li>Možnost grilování — gril si přivezte vlastní nebo zapůjčíme</li>
      </ul>
    ),
  },
  {
    title: '7. Domácí mazlíčci',
    body: (
      <p>
        Psi do <b>10 kg</b> jsou vítáni <b>bez poplatku</b>. V areálu prosím na vodítku
        a po svém pejskovi uklízejte.
      </p>
    ),
  },
  {
    title: '8. Klid a chování v areálu',
    body: (
      <>
        <p>
          Noční klid platí <b>od 22:00</b> — hudba a hlasitý provoz pouze do této hodiny.
          Chovejte se prosím ohleduplně k ostatním hostům i k přírodě.
        </p>
        <p className="mt-2">
          Host odpovídá za škodu, kterou způsobí na majetku ubytovatele. Vzniklou škodu
          nahlaste neprodleně obsluze.
        </p>
      </>
    ),
  },
  {
    title: '9. Platba a storno',
    body: (
      <p>
        Způsob platby, zálohy a storno podmínky najdete v{' '}
        <a href="#podminky" className="font-semibold text-ink-900 hover:text-brand-600">
          Obchodních podmínkách
        </a>
        . Ceny všech služeb jsou v{' '}
        <a href="#info-cenik" className="font-semibold text-ink-900 hover:text-brand-600">
          Celkovém ceníku
        </a>
        .
      </p>
    ),
  },
]

export function UbytovaciRad() {
  return (
    <section className="bg-paper py-12 md:py-20 min-h-screen">
      <div className="container-x max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.30em] text-brand-500 font-bold flex items-center gap-2">
          <span className="inline-block w-8 h-px bg-brand-500" />
          Sobáčov · Fishing &amp; Sport Resort
        </div>
        <h1 className="h-display text-4xl md:text-5xl mt-3">Ubytovací řád</h1>
        <p className="text-ink-700 mt-3 text-sm">
          Ubytováním v areálu host souhlasí s tímto řádem. Děkujeme, že jej dodržujete —
          díky tomu si pobyt užijí všichni.
        </p>

        <div className="mt-10 space-y-8">
          {BLOCKS.map((b) => (
            <div key={b.title} className="card p-6 md:p-7">
              <h2 className="font-bold text-lg text-ink-900 tracking-tight">{b.title}</h2>
              <div className="mt-3 text-[15px] text-ink-800 leading-relaxed">{b.body}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-[13px] text-ink-700">
          Provozovatel: STALAGMIT, a.s., IČ 27377342 · Sobáčov 111, 783 21 Mladeč ·
          recepce <a href="tel:+420775140705" className="font-semibold text-ink-900 hover:text-brand-600">+420 775 140 705</a>,
          rybolov a chatky <a href="tel:+420775433401" className="font-semibold text-ink-900 hover:text-brand-600">+420 775 433 401</a>.
        </div>

        <a href="#top" onClick={() => { window.location.hash = '' }} className="btn-outline-dark mt-8">
          Zpět na hlavní stránku
        </a>
      </div>
    </section>
  )
}
